import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {FormProvider, useForm} from 'react-hook-form'
import {
    Badge
    , Box
    , Button
    , Card
    , CardActions, CardContent
    , CardHeader
    , Collapse
    , Drawer
    , IconButton
    , List
    , ListItem
    , ListItemText
    , Skeleton
    , styled
    , Typography
    , useTheme
} from '@mui/material/'
import useHttp from '../hooks/useHttp'
import useLogout from '../hooks/useLogout'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import customParseFormat from 'dayjs/plugin/customParseFormat' // ES 2015
import FilterFormElement from '../components/forms/FilterFormElement'
import OrganizationIcon from '../components/OrganizationIcon'
import {useActionLogs} from '../store/actionLogsContext'
import {ThemeContext} from '../App'
import {Divider} from "@mui/material";
import SvgCustomIcon from "../components/icons/SvgCustomIcon";

dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

// eslint-disable-next-line no-restricted-globals
// const logsBaseUrl = new URL('/api/action_log', location)
// eslint-disable-next-line no-restricted-globals
const getUsersUrl = new URL('/api/users', location)

const StyledDrawer = styled(Drawer)(({theme}) => ({
    width: theme.mixins.formDrawerWidth
    , '& .MuiDrawer-paper': {
        width: theme.mixins.formDrawerWidth
        , height: '100vh'
    }
}))

const StyledBadge = styled(Badge)(() => ({
    '& .MuiBadge-badge': {top: 10}
}))

const ExpandMore = styled(props => {
    const {expand, ...other} = props
    return <IconButton {...other} />
})(({theme}) => ({
    marginLeft: 'auto'
    , transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest
    })
    , variants: [
        {
            props: ({expand}) => !expand
            , style: {
                transform: 'rotate(0deg)'
            }
        }
        , {
            props: ({expand}) => !!expand
            , style: {
                transform: 'rotate(180deg)'
            }
        }
    ]
}))

const TableSkel = ({length = 5}) => ( // ? <CircularProgress sx={{ mt: 2 }} />
    <>
        <Skeleton width='100%' sx={{mt: 2, fontSize: '2rem'}}/>
        {Array.from({length}).map((_, i) => <Skeleton key={i} width='100%'/>)}
        {/* <Skeleton width='100%' sx={{ fontSize: '2rem' }} /> */}
    </>
)

export default function History({position, open, isMobile, getCrash}) {
    const navigate = useNavigate()
    const location = useLocation()
    const logout = useLogout()
    const {logs, setAsRead, fetchLogs} = useActionLogs()
    const state = location.state
    const {token = null} = state
    const theme = useTheme()
    const {_} = useLingui()
    const {isLoading, error, sendRequest} = useHttp()
    const [expanded, setExpanded] = useState(false)
    const methods = useForm({
        defaultValues: useMemo(elements => elements, [])
    })
    const submitRef = useRef()
    const [users, setUsers] = useState([])
    const {mode, toggleTheme} = useContext(ThemeContext)

    const historyFilterElements = [
        {
            label: _(msg`Date`)
            , isRequired: false
            , margin: 'dense'
            , type: 'date'
            , value: ''
            , stack: true
        }
        , {
            label: _(msg`User`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                {label: _(msg`None`), value: 0}
                , ...users?.map(user => ({label: user.username, value: user.id}))
            ]
        }
        , {
            label: _(msg`Action`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                {label: _(msg`None`), value: 0}
                , {label: _(msg`Added crash`), value: 'Added crash'}
                , {label: _(msg`Validated crash`), value: 'Validated crash'}
                , {label: _(msg`Registered new user`), value: 'Registered new user'}
                , {label: _(msg`Modified user`), value: 'Modified user'}
            ]
        }
        , {
            label: _(msg`Data`)
            , isRequired: false
            , margin: 'dense'
            , type: 'select'
            , value: 0
            , options: [
                {label: _(msg`None`), value: 0}
                , {label: _(msg`Police`), value: 1}
                , {label: _(msg`Healthcare`), value: 2}
                , {label: _(msg`Insurance`), value: 3}
            ]
        }
    ]

    const handleExpandClick = () => {
        setExpanded(!expanded)
    }

    const getUsers = useCallback(() => {
        sendRequest({
            token
            , url: getUsersUrl
            , manageResData: res => {
                setUsers(res.users)
            }
        })
    }, [token, sendRequest])

    useEffect(() => {
        if (!token) return logout()
        if (isMobile) return navigate('/home', {state})
        getUsers()
    }, [token, logout, isMobile, navigate, state, getUsers])

    const handleFilterObjSubmit = params => {
        let fetchParams = []

        Object.entries(params)
            .filter(([_, value]) => value ?? value)
            .filter(([_, value]) => value !== 'None')
            .forEach(([key, value]) => {
                switch (key) {
                    case 'User':
                        fetchParams = [...fetchParams, ['user_id', value]]
                        break
                    case 'Date-From':
                        fetchParams = [...fetchParams, ['after', dayjs(value).toJSON()]]
                        break
                    case 'Date-To':
                        fetchParams = [...fetchParams, ['before', dayjs(value).toJSON()]]
                        if (dayjs(fetchParams.find(fp => fp[0] === 'after')?.[1]).isValid &&
                            dayjs(fetchParams.find(fp => fp[0] === 'after')?.[1]).isSame(dayjs(), 'day')) {
                            fetchParams = fetchParams.filter(fp => fp[0] !== 'before')
                        }
                        break
                    case 'Action':
                        fetchParams = [...fetchParams, ['message', value]]
                        break
                    case 'Data':
                        fetchParams = [...fetchParams, ['organization', value]]
                        break
                    default:
                }
            })

        fetchLogs(fetchParams)
    }

    return (
        <StyledDrawer
            variant="persistent"
            anchor='right'
            open={open}
        >
            <Box sx={{p: 1}}>
                <Typography
                    sx={{color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white', fontWeight: 'bold', fontSize: 20, textAlign: 'center'}}>
                    <Trans>History</Trans>
                </Typography>
                <Divider sx={{mt:2,color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'}}/>
            </Box>

            {isLoading
                ? <TableSkel length={9}/>
                : <>
                    <Box>
                        <Card sx={{m: 1}}>
                            <CardHeader
                                onClick={handleExpandClick}
                                sx={{cursor: 'pointer'}}
                                action={
                                    <ExpandMore
                                        sx={{color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'}}
                                        expand={expanded}
                                        aria-expanded={expanded}
                                        aria-label='show more'
                                    >
                                        <ExpandMoreIcon/>
                                    </ExpandMore>
                                }
                                titleTypographyProps={{fontSize: 14,fontWeight:'bold',color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'}}
                                title='Filter'
                            />
                            <Collapse sx={{boxShadow: 'none'}} in={expanded} timeout='auto' unmountOnExit>
                                <FormProvider {...methods}>
                                    <CardContent>
                                        <Box
                                            ref={submitRef}
                                            component='form'
                                            onSubmit={methods.handleSubmit(handleFilterObjSubmit)}
                                            display='flex'
                                            flexDirection='column'
                                            alignItems={'center'}
                                            pl={1}
                                            pr={1}
                                            noValidate
                                        >
                                            {historyFilterElements
                                                .map(elem => <FilterFormElement
                                                    key={elem.label}
                                                    elem={elem}
                                                    isMobile={isMobile}
                                                />)}
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{justifyContent: 'flex-end'}}>
                                        <Button
                                            variant='contained'
                                            size='small'
                                            sx={{background: theme.palette.newColors.cyanGradient,color: theme.palette.newColors.lightGray}}
                                            onClick={() => submitRef.current && submitRef.current.requestSubmit()}
                                        >
                                            Filter
                                        </Button>
                                        <Button
                                            variant='contained'
                                            size='small'
                                            sx={{background: theme.palette.newColors.cyanGradient,color: theme.palette.newColors.lightGray}}
                                            onClick={() => {
                                                methods.reset()
                                                fetchLogs()
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </CardActions>
                                </FormProvider>
                            </Collapse>
                        </Card>
                    </Box>
                    <Box overflow='auto'>
                        {logs && logs.length ? (
                                <List>
                                    {logs.map((log, index) => {
                                        console.log('log --- ',log)
                                        const iconName = log.organization === 0 ? 'shieldPerson' : log.organization === 1 ? 'police' : 'insurance'
                                        return (
                                            <ListItem
                                                onClick={async () => {
                                                    if (!log.status) await setAsRead(log.id)
                                                    if (log.crash_id) getCrash(log.crash_id, log.organization, log.user_id)
                                                    if (log.message.includes('user')) navigate('/admin', {state: {...state}})
                                                }}
                                                key={log.id}
                                                sx={{
                                                    flexDirection: 'column'
                                                    ,
                                                    alignItems: 'stretch'
                                                    // , backgroundColor: index % 2 === 0 ? theme.palette.grey[200] : theme.palette.background.default
                                                    ,
                                                    backgroundColor: index % 2 === 0 ? theme.palette.grey[200] : theme.palette.background.default
                                                    ,
                                                    cursor: log.crash_id || log.message.includes('user') ? 'pointer' : 'default'
                                                }}>
                                                <StyledBadge color='info' badgeContent=' ' invisible={!!log.status}>
                                                    <SvgCustomIcon name={iconName} />
                                                </StyledBadge>
                                                <ListItemText sx={{alignSelf: 'flex-start'}}
                                                              secondary={`${log.username} ${dayjs(log.ts).fromNow()}`}/>
                                                <ListItemText primaryTypographyProps={{fontSize: 14}}
                                                              primary={log.message}/>
                                            </ListItem>
                                        )
                                    })}
                                </List>)
                            : <Typography mt={2} color={theme.palette.text.primary} textAlign='center'>
                                <Trans>LOGS</Trans>
                            </Typography>
                        }
                    </Box>
                </>}
        </StyledDrawer>
    )
}
